import { selector } from "recoil";
import { unifiedAcademicDataState } from "./unifiedAcademicDataAtom";
import { unifiedCourseDataState } from "./unifiedCourseDataAtom";
import { localSelectedCoursesSemKeyState } from "./localSelectedCoursesSemKeyAtom";
import {
  curriculumPlanState,
  sortSemesters,
  parseSemesterKey,
  getSemesterStatus,
} from "./curriculumPlanAtom";
import { findMainProgram } from "../helpers/academicDataTransformers";
import { processExerciseGroupECTS } from "../helpers/smartExerciseGroupHandler";

// ────────────────────────────────────────────────────────────────────────────
// Pure helper functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Normalize semester key by removing spaces (e.g., "FS 25" -> "FS25")
 * Handles inconsistency between scorecard API (with spaces) and internal state (without)
 */
const normalizeSemesterKey = (semester) => {
  return semester ? semester.replace(/\s+/g, "") : "";
};

/**
 * Idempotent credit normalization.
 * API returns raw values (e.g., 600 for 6 ECTS). Some paths pre-normalize.
 * This handles both: values > 99 are divided by 100, others pass through.
 * When credits are unknown (raw == null), returns `fallback`. The curriculum
 * map passes `null` so unresolved credits render as "?" rather than a
 * fabricated number.
 */
export const normalizeCourseCredits = (raw, fallback = 3) => {
  if (raw == null) return fallback;
  return raw > 99 ? raw / 100 : raw;
};

/**
 * Resolve a course's raw credits from ANY loaded semester catalog.
 *
 * A course is placed under one semester, but its credits are only resolvable if
 * that semester's catalog is loaded. Projected future semesters (and terms whose
 * catalog errors) have no catalog, so a course there can't resolve locally — even
 * though the same course number exists in another loaded catalog (a course's ECTS
 * is stable across terms). This is the last-resort lookup for that case.
 */
export const findCreditsInAnySemester = (courseId, semesters) => {
  if (!courseId) return null;
  for (const semData of Object.values(semesters || {})) {
    const hit = (semData?.available || []).find(
      (c) => c.courseNumber === courseId || c.id === courseId,
    );
    if (hit && hit.credits != null) return hit.credits;
  }
  return null;
};

/**
 * Extract classification values that should map to a category.
 * Uses category name/description patterns to infer valid classifications.
 */
const extractClassifications = (categoryItem) => {
  const classifications = [];
  const name = (
    categoryItem.description ||
    categoryItem.shortName ||
    ""
  ).toLowerCase();

  const patterns = [
    {
      keywords: ["compulsory", "pflicht", "mandatory"],
      values: [
        "compulsory",
        "pflicht",
        "Pflichtbereich",
        "mandatory",
        "Pflicht",
      ],
    },
    {
      keywords: ["context", "kontext"],
      values: ["context", "kontext", "Contextual Studies", "Kontextstudium"],
    },
    {
      keywords: ["focus", "schwerpunkt", "major", "concentration"],
      values: [
        "focus",
        "schwerpunkt",
        "Focus Area",
        "Major",
        "Concentration",
        "Schwerpunktfächer",
      ],
    },
    {
      keywords: ["method", "forschung", "research"],
      values: ["method", "forschung", "Research Methods", "Forschungsmethoden"],
    },
    {
      keywords: ["thesis", "arbeit", "dissertation"],
      values: [
        "thesis",
        "masterarbeit",
        "bachelorarbeit",
        "Thesis",
        "Master Thesis",
        "Bachelor Thesis",
      ],
    },
    {
      keywords: ["project", "projekt"],
      values: ["project", "projekt", "Projects", "Projekte"],
    },
    {
      keywords: ["core", "kern", "foundation"],
      values: ["core", "kern", "Core", "Foundation", "Kernbereich"],
    },
    {
      keywords: ["skill", "kompetenz", "competence"],
      values: [
        "skills",
        "Skills",
        "kompetenz",
        "Kompetenz",
        "Handlungskompetenz",
        "Competences",
      ],
    },
    { keywords: ["seminar"], values: ["seminar", "Seminar", "Seminare"] },
    {
      keywords: ["integration", "capstone"],
      values: ["integration", "capstone", "Integrationsfächer", "Capstone"],
    },
  ];

  for (const { keywords, values } of patterns) {
    if (keywords.some((kw) => name.includes(kw))) {
      classifications.push(...values);
    }
  }

  // "elective"/"wahl" must exclude "pflicht" to avoid matching "Wahlpflicht"
  if (
    (name.includes("elective") || name.includes("wahl")) &&
    !name.includes("pflicht")
  ) {
    classifications.push("elective", "wahl", "Wahlbereich", "Elective", "Wahl");
  }

  return classifications;
};

/**
 * Recursively extract category structure from scorecard items
 */
const extractCategoryHierarchy = (items, parentPath = "", level = 0) => {
  const categories = [];

  (items || []).forEach((item) => {
    if (item.isTitle) {
      const name = item.description || item.shortName || "Unknown";
      const path = parentPath ? `${parentPath}/${name}` : name;

      const category = {
        id: item.hierarchy || path,
        path,
        name,
        level,
        minCredits: parseFloat(item.minCredits) || 0,
        maxCredits: parseFloat(item.maxCredits) || 0,
        earnedCredits: parseFloat(item.sumOfCredits) || 0,
        validClassifications: extractClassifications(item),
        plannedCredits: 0,
        children: [],
      };

      if (item.items && Array.isArray(item.items)) {
        category.children = extractCategoryHierarchy(
          item.items,
          path,
          level + 1,
        );
      }

      categories.push(category);
    }
  });

  return categories;
};

/**
 * Extract courses (non-title items) with their semester and category info
 */
const extractCoursesFromHierarchy = (
  items,
  categoryPath = "",
  parentHierarchy = "",
) => {
  const courses = [];

  (items || []).forEach((item) => {
    if (item.isTitle) {
      const subPath = categoryPath
        ? `${categoryPath}/${item.description || item.shortName}`
        : item.description || item.shortName;

      if (item.items) {
        courses.push(
          ...extractCoursesFromHierarchy(item.items, subPath, item.hierarchy),
        );
      }
    } else if (!item.isTitle && item.semester) {
      courses.push({
        id: item.hierarchy || item.shortName || item.description,
        courseId: item.courseNumber || item.id || item.hierarchy,
        name: item.description || item.shortName,
        credits: parseFloat(item.sumOfCredits) || 0,
        semester: item.semester,
        categoryPath,
        categoryHierarchy: parentHierarchy,
        grade: item.mark || item.gradeText,
        gradeText: item.gradeText,
        status: item.gradeText ? "completed" : "enrolled",
        isCompleted: !!item.gradeText,
        classification: item.classification,
      });
    }
  });

  return courses;
};

/**
 * Calculate the maximum depth of a category hierarchy
 */
const getHierarchyDepth = (categories, currentDepth = 0) => {
  if (!categories?.length) return currentDepth;

  let maxDepth = currentDepth;
  categories.forEach((cat) => {
    if (cat.children?.length > 0) {
      const childDepth = getHierarchyDepth(cat.children, currentDepth + 1);
      maxDepth = Math.max(maxDepth, childDepth);
    }
  });

  return maxDepth;
};

/**
 * Flatten categories to leaf nodes for grid columns.
 * Skips single program-wrapper roots. Tracks grouping parent for header display.
 */
const flattenCategoriesForGrid = (categories) => {
  const flattened = [];

  const shouldSkipLevel0 =
    categories.length === 1 && categories[0].children.length > 0;

  const effectiveCategories = shouldSkipLevel0
    ? categories[0].children
    : categories;

  const recurse = (cats, depth, groupingParentId = null, ancestors = []) => {
    cats.forEach((cat) => {
      const parentIdForChildren = depth === 0 ? cat.id : groupingParentId;
      const isLeaf = !cat.children?.length;

      if (isLeaf) {
        flattened.push({
          ...cat,
          children: [],
          topLevelParentId: depth === 0 ? null : groupingParentId,
          // Grouping nodes are not grid rows, so a classification naming one
          // can only be resolved through the leaves that sit beneath it.
          ancestors,
        });
      } else {
        recurse(cat.children, depth + 1, parentIdForChildren, [
          ...ancestors,
          { id: cat.id, name: cat.name },
        ]);
      }
    });
  };

  recurse(effectiveCategories, 0, null, []);
  return flattened;
};

// ────────────────────────────────────────────────────────────────────────────
// Credit requirements
//
// A scorecard requirement node encodes a RANGE, not a single target:
// `minCredits` is the floor you must reach, `maxCredits` the ceiling that
// counts toward the parent. Anything above the ceiling is earned but does not
// count.
//
// A `maxCredits` of 0 means "no ceiling", NOT "counts for nothing" — the
// university's own roll-up confirms it: in Scorecard_BBWL, Contextual Studies
// reports sumOfCredits 13, which includes all 4 credits from its Languages
// child even though that child is min 0 / max 0. Such a node simply carries
// neither a floor nor a ceiling.
// ────────────────────────────────────────────────────────────────────────────

/** Credits a category must reach to count as satisfied. 0 = no requirement. */
export const getRequirementThreshold = ({
  minCredits = 0,
  maxCredits = 0,
} = {}) => (minCredits > 0 ? minCredits : maxCredits || 0);

const getTotal = ({ earnedCredits = 0, plannedCredits = 0 } = {}) =>
  earnedCredits + plannedCredits;

/**
 * True when a category has a real requirement and meets it. Drives the green
 * treatment, so a category with no requirement is never "complete".
 */
const meetsRequirement = (category) => {
  const threshold = getRequirementThreshold(category);
  return threshold > 0 && getTotal(category) >= threshold;
};

/**
 * True when a category has an explicit minimum and is below it. Drives the
 * parent gate.
 *
 * Keys off `minCredits` alone, NOT `getRequirementThreshold`: a maximum is a
 * ceiling, not an obligation, so a min-0/max-12 bucket ("Skills") sitting at 4
 * is under-filled but not deficient and must not hold its parent back.
 *
 * Consequently this is not the negation of `meetsRequirement` — a category can
 * both fail to be complete and fail to fall short. That asymmetry is what keeps
 * parents of unconstrained leaves reachable.
 */
const fallsShort = (category) =>
  (category?.minCredits || 0) > 0 && getTotal(category) < category.minCredits;

/** A category's ceiling. `Infinity` when it declares none. */
const getCreditCap = ({ maxCredits = 0 } = {}) =>
  maxCredits > 0 ? maxCredits : Infinity;

/**
 * Roll leaves up into a parent. Each leaf contributes at most its own ceiling;
 * the remainder is reported as `excessCredits` rather than inflating the total.
 */
const summarizeLeafCredits = (leaves = []) =>
  leaves.reduce(
    (acc, leaf) => {
      const total = getTotal(leaf);
      const counted = Math.min(total, getCreditCap(leaf));

      acc.countedTotal += counted;
      acc.excessCredits += total - counted;
      acc.allLeavesMeetRequirement =
        acc.allLeavesMeetRequirement && !fallsShort(leaf);
      return acc;
    },
    { countedTotal: 0, excessCredits: 0, allLeavesMeetRequirement: true },
  );

/** "12–27" for a range, "15" when the bounds coincide, "" when unconstrained. */
export const formatCreditRange = ({ minCredits = 0, maxCredits = 0 } = {}) => {
  if (!minCredits && !maxCredits) return "";
  if (!minCredits || !maxCredits || minCredits === maxCredits) {
    return String(getRequirementThreshold({ minCredits, maxCredits }));
  }
  return `${minCredits}–${maxCredits}`;
};

/**
 * Progress bar fill, denominated by the completion threshold so the bar reaches
 * 100% exactly when the requirement is met. A category with min 12 / max 24
 * sitting at 20 therefore shows a full bar while its label still advertises 24
 * — intended: the bar tracks the requirement, the label shows the range.
 */
export const getFillPercentage = (counted, category) => {
  const threshold = getRequirementThreshold(category);
  return threshold > 0 ? Math.min(100, Math.round((counted / threshold) * 100)) : 0;
};

/**
 * Build hierarchical category structure for nested grid headers.
 * Groups leaf categories under their parent for colspan display.
 */
const buildCategoryHierarchy = (categories, flatCategories) => {
  if (!categories?.length) return [];

  const shouldSkipLevel0 =
    categories.length === 1 && categories[0].children.length > 0;

  const groupingCategories = shouldSkipLevel0
    ? categories[0].children
    : categories;

  const categoriesByParent = {};
  flatCategories.forEach((cat) => {
    const parentId = cat.topLevelParentId || "root";
    if (!categoriesByParent[parentId]) {
      categoriesByParent[parentId] = [];
    }
    categoriesByParent[parentId].push(cat);
  });

  return groupingCategories.map((groupCat) => {
    const descendantLeaves = categoriesByParent[groupCat.id] || [];
    const isLeaf = descendantLeaves.length === 0;
    const leaves = isLeaf ? [groupCat] : descendantLeaves;

    const {
      countedTotal: leafTotal,
      excessCredits: leafExcess,
      allLeavesMeetRequirement,
    } = summarizeLeafCredits(leaves);

    // The parent has a ceiling of its own, so credits can be lost twice: once
    // to a child's cap, then again to the parent's. Both surface in the badge.
    const countedTotal = Math.min(leafTotal, getCreditCap(groupCat));
    const excessCredits = leafExcess + (leafTotal - countedTotal);

    // Complete only when the credits that actually count meet this node's own
    // requirement AND no leaf below it is short of its own minimum. Without the
    // second condition an over-filled category masks a starved sibling.
    const targetCredits = getRequirementThreshold(groupCat);
    const isComplete =
      targetCredits > 0 &&
      countedTotal >= targetCredits &&
      allLeavesMeetRequirement;

    return {
      id: groupCat.id,
      name: groupCat.name,
      path: groupCat.path,
      colspan: Math.max(1, leaves.length),
      isLeaf,
      minCredits: groupCat.minCredits,
      maxCredits: groupCat.maxCredits,
      countedTotal,
      excessCredits,
      isComplete,
      children: leaves.map((leaf) => ({
        id: leaf.id,
        name: leaf.name,
        path: leaf.path,
        minCredits: leaf.minCredits,
        maxCredits: leaf.maxCredits,
        earnedCredits: leaf.earnedCredits,
        plannedCredits: leaf.plannedCredits,
        validClassifications: leaf.validClassifications,
      })),
    };
  });
};

/**
 * Credit stats for one semester's courses.
 *
 * `plannedCredits` counts only wishlist courses and placeholders — courses the
 * user merely intends to take. Enrolled (in-progress) courses are real
 * commitments: they count toward `totalCredits` but never toward planned.
 */
const computeSemesterCreditStats = (semesterCourses) => {
  const sumCredits = (courses) =>
    courses.reduce((sum, c) => sum + (c.credits || 0), 0);

  return {
    totalCredits: sumCredits(semesterCourses),
    completedCredits: sumCredits(semesterCourses.filter((c) => c.isCompleted)),
    plannedCredits: sumCredits(
      semesterCourses.filter(
        (c) => c.status === "planned" || c.status === "placeholder",
      ),
    ),
  };
};

/** True when `classification` is covered by a category's keyword list. */
const matchesValidClassification = (category, lowerClassification) =>
  category.validClassifications.some(
    (vc) =>
      vc.toLowerCase() === lowerClassification ||
      lowerClassification.includes(vc.toLowerCase()),
  );

/**
 * Leaves sitting under a grouping (non-leaf) category of that name. Grid rows
 * are leaves only, so an ancestor name is otherwise unreachable.
 */
const findLeavesUnderAncestor = (lowerClassification, flatCategories) =>
  flatCategories.filter((cat) =>
    cat.ancestors.some((a) => a.name.toLowerCase() === lowerClassification),
  );

/** Best guess among several leaves of one ancestor, using the classification alone. */
const pickLeafUnderAncestor = (lowerClassification, candidates) =>
  candidates.find((cat) =>
    lowerClassification.includes(cat.name.toLowerCase()),
  ) ||
  candidates.find((cat) =>
    matchesValidClassification(cat, lowerClassification),
  ) ||
  candidates[0];

/**
 * Match a classification (or a course-name prefix) to a grid category.
 *
 * Order: exact leaf name → ancestor (grouping) name resolved to a leaf, its
 * only one or the best guess among several → keyword/substring match against a
 * leaf's validClassifications.
 *
 * @returns {object|undefined} The matched category, or undefined if no match
 */
const matchClassificationToCategory = (classification, flatCategories) => {
  if (!classification) return undefined;
  const lowerClassification = classification.toLowerCase();
  const underAncestor = findLeavesUnderAncestor(
    lowerClassification,
    flatCategories,
  );

  return (
    flatCategories.find(
      (cat) => cat.name.toLowerCase() === lowerClassification,
    ) ||
    (underAncestor.length
      ? pickLeafUnderAncestor(lowerClassification, underAncestor)
      : undefined) ||
    flatCategories.find((cat) =>
      matchesValidClassification(cat, lowerClassification),
    )
  );
};

/** Course names carry their category as a prefix: "Skills: Julia - …". */
const MAX_PREFIX_WORDS = 3;

/**
 * Extract the category prefix from a course name, i.e. the text before the
 * first colon when it is short enough to be a category rather than a title.
 *
 * @returns {string|null} The prefix, or null when the name carries none
 */
export const inferClassificationFromCourseName = (name) => {
  if (!name) return null;
  const colonIndex = name.indexOf(":");
  if (colonIndex <= 0) return null;

  const prefix = name.slice(0, colonIndex).trim();
  if (!prefix) return null;

  return prefix.split(/\s+/).length <= MAX_PREFIX_WORDS ? prefix : null;
};

const FOCUS_AREA_KEYWORDS = [
  "focus",
  "schwerpunkt",
  "concentration",
  "area of concentration",
];

/**
 * Programs without a skills bucket file "Skills:" courses under their focus
 * area. (A skills bucket itself is already reachable by keyword.)
 */
const findFocusAreaLeaf = (flatCategories) =>
  flatCategories.find((cat) =>
    FOCUS_AREA_KEYWORDS.some(
      (kw) =>
        cat.name.toLowerCase().includes(kw) ||
        cat.validClassifications.some((vc) => vc.toLowerCase().includes(kw)),
    ),
  );

/** Resolve a course-name prefix against a set of leaves. */
const matchPrefix = (prefix, categories) =>
  matchClassificationToCategory(prefix, categories) ||
  (prefix.toLowerCase() === "skills" ? findFocusAreaLeaf(categories) : undefined);

/**
 * Resolve the grid category for a course: the classification first, the
 * category prefix carried by the course name second.
 *
 * The one wrinkle is an ambiguous ancestor: when the classification names a
 * grouping node with several leaves, the prefix breaks the tie — but only among
 * that node's leaves. The classification is what the university says about the
 * enrolment, so it always wins the group; the prefix reaches other branches only
 * when the classification matched nothing at all.
 *
 * @returns {object|undefined} The matched category, or undefined if no match
 */
export const resolveCategoryForCourse = ({
  classification,
  courseName,
  flatCategories = [],
}) => {
  const prefix = inferClassificationFromCourseName(courseName);
  const lowerClassification = (classification || "").toLowerCase();

  const isExactLeaf = flatCategories.some(
    (cat) => cat.name.toLowerCase() === lowerClassification,
  );
  const ambiguousAncestor =
    lowerClassification && !isExactLeaf
      ? findLeavesUnderAncestor(lowerClassification, flatCategories)
      : [];

  if (prefix && ambiguousAncestor.length > 1) {
    const tiebreak = matchPrefix(prefix, ambiguousAncestor);
    if (tiebreak) return tiebreak;
  }

  return (
    matchClassificationToCategory(classification, flatCategories) ||
    (prefix ? matchPrefix(prefix, flatCategories) : undefined)
  );
};

/**
 * Least-wrong bucket for a course we could not classify. An elective leaf does
 * not inflate compulsory progress, so it is preferred over the first column.
 *
 * @returns {object|undefined} The fallback category, or undefined if none
 */
export const resolveFallbackCategory = (flatCategories = []) =>
  flatCategories.find((cat) =>
    cat.validClassifications.some((vc) => vc.toLowerCase() === "elective"),
  ) || flatCategories[flatCategories.length - 1];

/**
 * The category a plan placement pins a course to, when that path is a real grid
 * row. Returns null when the course has no usable placement.
 *
 * @returns {string|null} A valid category path, or null
 */
const findPlacementCategoryPath = (plannedItems, courseId, flatCategories) => {
  const placement = (plannedItems || []).find(
    (item) => item.type === "course" && item.courseId === courseId,
  );
  if (!placement?.categoryPath) return null;

  return flatCategories.some((cat) => cat.path === placement.categoryPath)
    ? placement.categoryPath
    : null;
};

/**
 * Estimate completion semester based on credits and planned courses
 */
const estimateCompletion = (required, earned, semesters) => {
  if (earned >= required) return "Completed";

  const remaining = required - earned;
  const avgCreditsPerSemester =
    semesters.length > 0
      ? semesters.reduce((sum, s) => sum + s.totalCredits, 0) / semesters.length
      : 30;

  const semestersNeeded = Math.ceil(
    remaining / Math.max(avgCreditsPerSemester, 15),
  );
  const futureSems = semesters.filter((s) => s.status === "future");

  if (futureSems.length >= semestersNeeded) {
    return futureSems[semestersNeeded - 1]?.key || "TBD";
  }

  const lastSemester = semesters[semesters.length - 1];
  if (!lastSemester) return "TBD";

  const parsed = parseSemesterKey(lastSemester.key);
  let projectedYear = parsed.year;
  let isHS = parsed.type === "HS";

  for (let i = 0; i < semestersNeeded - futureSems.length; i++) {
    if (!isHS) projectedYear++;
    isHS = !isHS;
  }

  return `${isHS ? "HS" : "FS"}${projectedYear}`;
};

// ────────────────────────────────────────────────────────────────────────────
// Empty state template
// ────────────────────────────────────────────────────────────────────────────

const createEmptyResult = (isLoaded, validations) => ({
  isLoaded,
  program: null,
  categories: [],
  flatCategories: [],
  categoryHierarchy: [],
  semesters: [],
  coursesBySemesterAndCategory: {},
  validations,
});

// ────────────────────────────────────────────────────────────────────────────
// Selector 1: Category structure (depends only on academic data)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Extracts the structural category data from the transcript scorecard.
 * This only depends on unifiedAcademicDataState, so it won't recompute
 * when courses are moved around in the grid.
 */
export const curriculumCategoryStructureSelector = selector({
  key: "curriculumCategoryStructureSelector",
  get: ({ get }) => {
    const academicData = get(unifiedAcademicDataState);

    if (!academicData.initialization?.isInitialized || !academicData.programs) {
      return {
        isReady: false,
        mainProgramId: null,
        rawScorecard: null,
        categories: [],
        flatCategories: [],
        completedCourses: [],
      };
    }

    const mainProgramId = findMainProgram(
      Object.fromEntries(
        Object.entries(academicData.programs).map(([id, p]) => [
          id,
          { isMainStudy: p.metadata?.isMainStudy },
        ]),
      ),
    );

    if (!mainProgramId) {
      return {
        isReady: false,
        mainProgramId: null,
        rawScorecard: null,
        categories: [],
        flatCategories: [],
        completedCourses: [],
      };
    }

    const mainProgram = academicData.programs[mainProgramId];
    const rawScorecard = mainProgram?.transcript?.rawScorecard;

    const categories = rawScorecard?.items
      ? extractCategoryHierarchy(rawScorecard.items)
      : [];

    if (import.meta.env.DEV && categories.length > 0) {
      const shouldSkipLevel0 =
        categories.length === 1 && categories[0].children.length > 0;
      const effectiveParents = shouldSkipLevel0
        ? categories[0].children
        : categories;
      const totalDepth = getHierarchyDepth(categories);
      console.log("[CurriculumMap] Category hierarchy structure:", {
        totalDepth,
        skippingProgramWrapper: shouldSkipLevel0,
        programName: shouldSkipLevel0 ? categories[0].name : "(none)",
        effectiveGroupingParents: effectiveParents.map((c) => ({
          name: c.name,
          level: c.level,
          hasChildren: c.children.length > 0,
          childCount: c.children.length,
          children: c.children.map((ch) => ch.name),
        })),
      });
    }

    const flatCategories = flattenCategoriesForGrid(categories);

    const completedCourses = rawScorecard?.items
      ? extractCoursesFromHierarchy(rawScorecard.items)
      : [];

    return {
      isReady: true,
      mainProgramId,
      rawScorecard,
      categories,
      flatCategories,
      completedCourses,
    };
  },
});

// ────────────────────────────────────────────────────────────────────────────
// Selector 2: Course grid assembly + stats (depends on all data sources)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Main curriculum map selector.
 * Combines category structure with course data from multiple sources
 * into a grid-ready structure for rendering.
 */
export const curriculumMapSelector = selector({
  key: "curriculumMapSelector",
  get: ({ get }) => {
    const categoryStructure = get(curriculumCategoryStructureSelector);
    const unifiedCourseData = get(unifiedCourseDataState);
    const localSelectedCourses = get(localSelectedCoursesSemKeyState);
    const curriculumPlan = get(curriculumPlanState);

    if (!categoryStructure.isReady) {
      return createEmptyResult(
        categoryStructure.mainProgramId !== null,
        curriculumPlan.validations,
      );
    }

    const {
      mainProgramId,
      rawScorecard,
      categories,
      flatCategories,
      completedCourses,
    } = categoryStructure;

    // ── Collect all semester keys ──────────────────────────────────────────
    const allSemesterKeys = new Set();

    completedCourses.forEach((c) =>
      allSemesterKeys.add(normalizeSemesterKey(c.semester)),
    );
    Object.keys(localSelectedCourses).forEach((k) =>
      allSemesterKeys.add(normalizeSemesterKey(k)),
    );
    Object.keys(curriculumPlan.plannedItems || {}).forEach((k) =>
      allSemesterKeys.add(normalizeSemesterKey(k)),
    );
    Object.keys(unifiedCourseData.semesters || {}).forEach((k) =>
      allSemesterKeys.add(normalizeSemesterKey(k)),
    );

    const sortedSemesters = sortSemesters(Array.from(allSemesterKeys));

    // ── Initialize grid ───────────────────────────────────────────────────
    const coursesBySemesterAndCategory = {};

    sortedSemesters.forEach((semKey) => {
      coursesBySemesterAndCategory[semKey] = {};
      flatCategories.forEach((cat) => {
        coursesBySemesterAndCategory[semKey][cat.path] = [];
      });
    });

    // ── Place completed courses ───────────────────────────────────────────
    completedCourses.forEach((course) => {
      const semKey = normalizeSemesterKey(course.semester);
      const catPath = course.categoryPath;

      if (coursesBySemesterAndCategory[semKey]) {
        const matchedCat = flatCategories.find(
          (c) => c.path === catPath || catPath.startsWith(c.path),
        );

        if (matchedCat) {
          coursesBySemesterAndCategory[semKey][matchedCat.path].push({
            ...course,
            semester: semKey,
            source: "transcript",
          });
        }
      }
    });

    // ── Place wishlist courses ────────────────────────────────────────────
    Object.entries(localSelectedCourses).forEach(([semKey, courses]) => {
      if (!coursesBySemesterAndCategory[semKey]) return;

      // Per-plan wishlist overrides: skip courses hidden in the active plan
      const removedIds =
        curriculumPlan.wishlistOverrides?.[semKey]?.removedCourseIds || [];

      const availableCourses =
        unifiedCourseData.semesters?.[semKey]?.available || [];

      courses.forEach((course) => {
        const courseId = course.id || course.courseNumber;
        if (removedIds.includes(courseId)) return;
        const fullCourse = availableCourses.find(
          (c) =>
            c.courseNumber === course.id ||
            c.id === course.id ||
            c.courseNumber === course.courseNumber,
        );

        const classification =
          course.classification ||
          fullCourse?.classification ||
          course.big_type ||
          "elective";

        // Use stored categoryPath first (from drag-and-drop), validate it exists
        let targetCatPath = course.categoryPath;

        if (
          targetCatPath &&
          !flatCategories.some((cat) => cat.path === targetCatPath)
        ) {
          targetCatPath = null;
        }

        // Fallback to classification matching
        if (!targetCatPath) {
          const matchedCat = resolveCategoryForCourse({
            classification,
            courseName:
              course.shortName || fullCourse?.shortName || fullCourse?.description,
            flatCategories,
          });
          targetCatPath =
            matchedCat?.path || resolveFallbackCategory(flatCategories)?.path;

          if (import.meta.env.DEV && !matchedCat) {
            console.warn(
              `[CurriculumMap] No category match for course "${course.shortName || course.id}"`,
              `\n  Classification: "${classification}"`,
              `\n  Available categories:`,
              flatCategories.map(
                (c) => `${c.name} [${c.validClassifications.join(", ")}]`,
              ),
            );
          }
        }

        if (
          targetCatPath &&
          coursesBySemesterAndCategory[semKey][targetCatPath]
        ) {
          coursesBySemesterAndCategory[semKey][targetCatPath].push({
            id: course.id || course.courseNumber,
            courseId: course.id || course.courseNumber,
            name: course.shortName || fullCourse?.shortName || course.id,
            credits: normalizeCourseCredits(
              course.credits ??
                fullCourse?.credits ??
                findCreditsInAnySemester(
                  course.id || course.courseNumber,
                  unifiedCourseData.semesters,
                ),
              null,
            ),
            semester: semKey,
            categoryPath: targetCatPath,
            status: "planned",
            isCompleted: false,
            isPlanned: true,
            classification,
            source: "wishlist",
            calendarEntry: course.calendarEntry || fullCourse?.calendarEntry,
          });
        }
      });
    });

    // ── Place enrolled courses ────────────────────────────────────────────
    // Courses rendered as enrolled cards, so the plan pass does not duplicate
    // them from their (override) placement.
    const enrolledCourseIdsBySemester = {};

    Object.entries(unifiedCourseData.semesters || {}).forEach(
      ([semKey, semData]) => {
        const normalizedSemKey = normalizeSemesterKey(semKey);
        if (!coursesBySemesterAndCategory[normalizedSemKey]) return;

        const enrolledIds = semData.enrolledIds || [];
        const availableCourses = semData.available || [];

        enrolledIds.forEach((enrolledId) => {
          const alreadyExists = Object.values(
            coursesBySemesterAndCategory[normalizedSemKey],
          )
            .flat()
            .some((c) => c.id === enrolledId || c.courseId === enrolledId);

          if (alreadyExists) return;

          const fullCourse = availableCourses.find(
            (c) => c.courseNumber === enrolledId || c.id === enrolledId,
          );

          if (!fullCourse) return;

          const classification =
            fullCourse.classification || fullCourse.big_type || "elective";

          // A plan placement for the same course is the student's own
          // correction and outranks any inferred category.
          const overridePath = findPlacementCategoryPath(
            curriculumPlan.plannedItems?.[normalizedSemKey],
            enrolledId,
            flatCategories,
          );

          const matchedCat =
            overridePath === null
              ? resolveCategoryForCourse({
                  classification,
                  courseName: fullCourse.shortName || fullCourse.description,
                  flatCategories,
                })
              : null;
          const targetCatPath =
            overridePath ??
            matchedCat?.path ??
            resolveFallbackCategory(flatCategories)?.path;

          enrolledCourseIdsBySemester[normalizedSemKey] ??= new Set();
          enrolledCourseIdsBySemester[normalizedSemKey].add(enrolledId);

          if (import.meta.env.DEV && !matchedCat && overridePath === null) {
            console.warn(
              `[CurriculumMap] No category match for enrolled course "${fullCourse.shortName || enrolledId}"`,
              `\n  Classification: "${classification}"`,
              `\n  Available categories:`,
              flatCategories.map(
                (c) => `${c.name} [${c.validClassifications.join(", ")}]`,
              ),
            );
          }

          if (
            targetCatPath &&
            coursesBySemesterAndCategory[normalizedSemKey][targetCatPath]
          ) {
            coursesBySemesterAndCategory[normalizedSemKey][targetCatPath].push({
              id: enrolledId,
              courseId: enrolledId,
              name:
                fullCourse.shortName || fullCourse.description || enrolledId,
              credits: normalizeCourseCredits(
                fullCourse.credits ??
                  findCreditsInAnySemester(
                    enrolledId,
                    unifiedCourseData.semesters,
                  ),
                null,
              ),
              semester: normalizedSemKey,
              categoryPath: targetCatPath,
              status: "enrolled",
              isCompleted: false,
              isEnrolled: true,
              classification,
              source: "enrolled",
              calendarEntry: fullCourse.calendarEntry,
            });
          }
        });
      },
    );

    // ── Place curriculum plan items ───────────────────────────────────────
    Object.entries(curriculumPlan.plannedItems || {}).forEach(
      ([semKey, items]) => {
        if (!coursesBySemesterAndCategory[semKey]) {
          coursesBySemesterAndCategory[semKey] = {};
          flatCategories.forEach((cat) => {
            coursesBySemesterAndCategory[semKey][cat.path] = [];
          });
        }

        items.forEach((item) => {
          // The enrolled pass already rendered this course (using this very
          // placement as its category override) — don't duplicate the card.
          if (
            item.type === "course" &&
            enrolledCourseIdsBySemester[normalizeSemesterKey(semKey)]?.has(
              item.courseId,
            )
          ) {
            return;
          }

          // First try the stored categoryPath
          let targetCatPath = item.categoryPath;

          // Check if categoryPath is a valid path in flatCategories
          if (
            targetCatPath &&
            !flatCategories.some((cat) => cat.path === targetCatPath)
          ) {
            // Try to find a category by matching the categoryPath as a name or partial path
            const matchedByName = flatCategories.find(
              (cat) =>
                cat.name === targetCatPath ||
                cat.path.endsWith(`/${targetCatPath}`) ||
                cat.path.includes(targetCatPath),
            );
            if (matchedByName) {
              targetCatPath = matchedByName.path;
            } else {
              // Fall back to classification matching for courses
              if (item.type === "course") {
                const availableCourses =
                  unifiedCourseData.semesters?.[semKey]?.available || [];
                const fullCourse = availableCourses.find(
                  (c) =>
                    c.courseNumber === item.courseId || c.id === item.courseId,
                );
                const classification =
                  fullCourse?.classification || item.categoryPath || "elective";
                const matchedCat = resolveCategoryForCourse({
                  classification,
                  courseName:
                    item.shortName ||
                    fullCourse?.shortName ||
                    fullCourse?.description,
                  flatCategories,
                });
                targetCatPath =
                  matchedCat?.path ||
                  resolveFallbackCategory(flatCategories)?.path ||
                  "";
              } else {
                targetCatPath = resolveFallbackCategory(flatCategories)?.path || "";
              }
            }
          }

          // Fall back to the least-wrong bucket if still no match
          if (!targetCatPath) {
            targetCatPath = resolveFallbackCategory(flatCategories)?.path || "";
          }

          if (coursesBySemesterAndCategory[semKey][targetCatPath]) {
            if (item.type === "placeholder") {
              coursesBySemesterAndCategory[semKey][targetCatPath].push({
                id: item.id,
                name: item.label || "TBD",
                label: item.label || "TBD",
                credits: item.credits,
                semester: semKey,
                categoryPath: targetCatPath,
                status: "placeholder",
                isPlaceholder: true,
                source: "plan",
                note: item.note,
                colorCode: item.colorCode,
              });
            } else if (item.type === "course") {
              const availableCourses =
                unifiedCourseData.semesters?.[semKey]?.available || [];
              const fullCourse = availableCourses.find(
                (c) =>
                  c.courseNumber === item.courseId || c.id === item.courseId,
              );

              coursesBySemesterAndCategory[semKey][targetCatPath].push({
                id: item.courseId,
                courseId: item.courseId,
                name: fullCourse?.shortName || item.shortName || item.courseId,
                shortName: item.shortName || fullCourse?.shortName,
                credits: normalizeCourseCredits(
                  item.credits ??
                    fullCourse?.credits ??
                    findCreditsInAnySemester(
                      item.courseId,
                      unifiedCourseData.semesters,
                    ),
                  null,
                ),
                semester: semKey,
                categoryPath: targetCatPath,
                status: "planned",
                isCompleted: false,
                isPlanned: true,
                source: "plan",
                calendarEntry: fullCourse?.calendarEntry,
                note: item.note,
                colorCode: item.colorCode,
              });
            }
          }
        });
      },
    );

    // ── Exercise group credit normalization ───────────────────────────────
    sortedSemesters.forEach((semKey) => {
      const allSemesterCourses = Object.values(
        coursesBySemesterAndCategory[semKey] || {},
      ).flat();

      if (allSemesterCourses.length === 0) return;

      const processedCourses = processExerciseGroupECTS(allSemesterCourses);

      const processedCreditsMap = new Map();
      processedCourses.forEach((course) => {
        const courseId = course.id || course.courseId;
        if (courseId) {
          processedCreditsMap.set(courseId, course.credits);
        }
      });

      Object.keys(coursesBySemesterAndCategory[semKey]).forEach((catPath) => {
        coursesBySemesterAndCategory[semKey][catPath] =
          coursesBySemesterAndCategory[semKey][catPath].map((course) => {
            const courseId = course.id || course.courseId;
            if (courseId && processedCreditsMap.has(courseId)) {
              return { ...course, credits: processedCreditsMap.get(courseId) };
            }
            return course;
          });
      });
    });

    // ── Calculate semester stats ──────────────────────────────────────────
    const semesters = sortedSemesters.map((semKey) => {
      const semesterCourses = Object.values(
        coursesBySemesterAndCategory[semKey] || {},
      ).flat();
      return {
        key: semKey,
        status: getSemesterStatus(semKey),
        ...computeSemesterCreditStats(semesterCourses),
        courseCount: semesterCourses.length,
        note: curriculumPlan.semesterNotes?.[semKey] || "",
      };
    });

    // ── Calculate category credit totals ──────────────────────────────────
    const categoriesWithCredits = flatCategories.map((cat) => {
      const earnedCredits = cat.earnedCredits || 0;
      let plannedCredits = 0;

      sortedSemesters.forEach((semKey) => {
        const courses = coursesBySemesterAndCategory[semKey]?.[cat.path] || [];
        courses.forEach((course) => {
          if (course.source !== "transcript") {
            plannedCredits += course.credits || 0;
          }
        });
      });

      return {
        ...cat,
        earnedCredits,
        plannedCredits,
        totalCredits: earnedCredits + plannedCredits,
        isComplete: meetsRequirement({ ...cat, earnedCredits, plannedCredits }),
      };
    });

    // ── Calculate program stats ───────────────────────────────────────────
    const totalEarned = categoriesWithCredits.reduce(
      (sum, c) => sum + c.earnedCredits,
      0,
    );
    const totalPlanned = categoriesWithCredits.reduce(
      (sum, c) => sum + c.plannedCredits,
      0,
    );
    const totalRequired =
      parseFloat(rawScorecard?.items?.[0]?.maxCredits) || 180;

    // ── Build hierarchy for nested headers ────────────────────────────────
    const categoryHierarchy = buildCategoryHierarchy(
      categories,
      categoriesWithCredits,
    );

    if (import.meta.env.DEV && categoryHierarchy.length > 0) {
      const hasNestedHeaders = categoryHierarchy.some(
        (p) => p.children.length > 1,
      );
      console.log("[CurriculumMap] Hierarchy result:", {
        hasNestedHeaders,
        parents: categoryHierarchy.map((p) => ({
          name: p.name,
          colspan: p.colspan,
          isLeaf: p.isLeaf,
          childCount: p.children.length,
          children: p.children.map((c) => c.name),
        })),
      });
    }

    return {
      isLoaded: true,
      program: {
        id: mainProgramId,
        name: mainProgramId,
        totalRequired,
        totalEarned,
        totalPlanned,
        completionPercentage: Math.round((totalEarned / totalRequired) * 100),
        estimatedCompletion: estimateCompletion(
          totalRequired,
          totalEarned,
          semesters,
        ),
      },
      categories,
      flatCategories: categoriesWithCredits,
      categoryHierarchy,
      semesters,
      coursesBySemesterAndCategory,
      validations: curriculumPlan.validations,
    };
  },
});

// ────────────────────────────────────────────────────────────────────────────
// Derived convenience selectors
// ────────────────────────────────────────────────────────────────────────────

/** Flat category list (useful for CoursePicker) */
export const curriculumCategoriesSelector = selector({
  key: "curriculumCategoriesSelector",
  get: ({ get }) => {
    const mapData = get(curriculumMapSelector);
    return mapData.flatCategories;
  },
});

/** Semester list with stats */
export const curriculumSemestersSelector = selector({
  key: "curriculumSemestersSelector",
  get: ({ get }) => {
    const mapData = get(curriculumMapSelector);
    return mapData.semesters;
  },
});

/** Program overview stats */
export const curriculumProgramSelector = selector({
  key: "curriculumProgramSelector",
  get: ({ get }) => {
    const mapData = get(curriculumMapSelector);
    return mapData.program;
  },
});

// ────────────────────────────────────────────────────────────────────────────
// Classification matching — exported for drag-and-drop suggestion highlighting
// ────────────────────────────────────────────────────────────────────────────

/**
 * Check if a course classification matches a specific category.
 * Uses the same matching logic as matchClassificationToCategory:
 * 1. Direct name match (case-insensitive)
 * 2. Fuzzy keyword match via validClassifications
 *
 * @param {string} classification - The course's classification string
 * @param {string} categoryName - The category name to check against
 * @param {string[]} validClassifications - The category's valid classification keywords
 * @returns {boolean}
 */
export const doesClassificationMatchCategory = (
  classification,
  categoryName,
  validClassifications,
) => {
  if (!classification || !validClassifications) return false;
  const lower = classification.toLowerCase();
  return (
    categoryName.toLowerCase() === lower ||
    validClassifications.some(
      (vc) => vc.toLowerCase() === lower || lower.includes(vc.toLowerCase()),
    )
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Exported for testing
// ────────────────────────────────────────────────────────────────────────────

export const _testHelpers = {
  normalizeSemesterKey,
  normalizeCourseCredits,
  extractClassifications,
  extractCategoryHierarchy,
  extractCoursesFromHierarchy,
  flattenCategoriesForGrid,
  buildCategoryHierarchy,
  meetsRequirement,
  fallsShort,
  summarizeLeafCredits,
  matchClassificationToCategory,
  resolveFallbackCategory,
  inferClassificationFromCourseName,
  resolveCategoryForCourse,
  estimateCompletion,
  computeSemesterCreditStats,
};
